import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  ContractChargeListsDto,
  NewShippingServiceAndContractRequestDto,
  NewShippingServiceDto,
  NoContractDraft,
  NoContractDraftData,
  NoContractResponseDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

export const useActiveNoContractDraft = (shipmentInvoiceId: string | null): UseQueryResult<NoContractDraft> =>
  useQuery(
    cacheKeysUtil.activeNoContractDraft(shipmentInvoiceId),
    async (): Promise<NoContractDraft> => {
      if (!shipmentInvoiceId) return null;
      const response = await tFinancialService.api.getActiveNoContractDraft(shipmentInvoiceId);
      const { data } = response;
      return data.data;
    },
    {
      enabled: !!shipmentInvoiceId,
    }
  );

export const useMatchingServicesForNoContract = (shipmentInvoiceId: string): UseQueryResult<NoContractResponseDto> =>
  useQuery(
    cacheKeysUtil.matchingServicesForNoContract(shipmentInvoiceId),
    async (): Promise<NoContractResponseDto> => {
      const response = await tFinancialService.api.getMatchingServicesForNoContractInvoice(shipmentInvoiceId);
      const { data } = response;
      return data.data;
    },
    {
      enabled: !!shipmentInvoiceId,
    }
  );

export const useContractChargesDetailsForShippingService = (contractFlowId: string): UseQueryResult<ContractChargeListsDto> =>
  useQuery(
    cacheKeysUtil.contractChargesDetailsForShippingService(contractFlowId),
    async (): Promise<ContractChargeListsDto> => {
      const response = await tFinancialService.api.getContractFlow(contractFlowId);
      const { data } = response;
      // ContractFlowInfoViewDto contains all the charge arrays we need
      return {
        containerCharges: data.data?.containerCharges,
        shipmentCharges: data.data?.shipmentCharges,
        declarationCharges: data.data?.declarationCharges,
        weightCharges: data.data?.weightCharges,
        percentageCharges: data.data?.percentageCharges,
      };
    },
    {
      enabled: !!contractFlowId,
    }
  );

export const useIsValidityPeriodValid = (invoiceId: string) =>
  useMutation(async (newShippingService: NewShippingServiceDto) => tFinancialService.api.isValidityPeriodValid(invoiceId, newShippingService));
export const useUpdateNoContractDraft = (shipmentInvoiceId: string) => {
  const queryClient = useQueryClient();

  return useMutation(async (data: NoContractDraftData) => tFinancialService.api.createOrUpdateNoContractDraft(shipmentInvoiceId, { data }), {
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries(cacheKeysUtil.activeNoContractDraft(shipmentInvoiceId))]);
    },
  });
};
export const useDiscardNoContractDraft = (shipmentInvoiceId: string) => {
  const queryClient = useQueryClient();

  return useMutation(async () => tFinancialService.api.discardNoContractDraft(shipmentInvoiceId), {
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries(cacheKeysUtil.activeNoContractDraft(shipmentInvoiceId))]);
    },
  });
};

export const useCreateShippingServiceAndContract = (shipmentInvoiceId: string) => {
  const queryClient = useQueryClient();

  return useMutation(async (data: NewShippingServiceAndContractRequestDto) => tFinancialService.api.createShippingServiceAndContract(shipmentInvoiceId, data), {
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries(cacheKeysUtil.activeNoContractDraft(shipmentInvoiceId))]);
    },
  });
};

export const useChargeCategorySuggestions = (shipmentInvoiceId?: string) =>
  useQuery(
    ['chargeCategorySuggestions', shipmentInvoiceId],
    async () => {
      const { data } = await tFinancialService.api.suggestChargeCategorization(shipmentInvoiceId!);
      return data;
    },
    {
      enabled: !!shipmentInvoiceId,
      staleTime: 5 * 60 * 1000,
    }
  );

export const useNrcfOptions = (billedEntityId?: number): UseQueryResult<string[]> =>
  useQuery(cacheKeysUtil.getNrcfOptions(billedEntityId), async (): Promise<string[]> => {
    const response = await tFinancialService.api.getNrcfOptions({ billedEntityId });
    const { data } = response;
    return data.data || [];
  });
